import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ForgotPassword from "./ForgotPassword";
import { UTILS, PBPUTILS } from "../config/API";
import appConfig, { getPortalKey } from "../config";
import tlingitLogo from "../assets/icons/tlingitRedBlack.png";
import BUILD_DATE from "../config/version";
import klLogo from "../assets/icons/teroKlLogin.png";

const storeUserDisplayName = (data) => {
  const firstName = (data?.firstname || "").trim();
  const lastName = (data?.lastname || "").trim();
  const displayName = [firstName, lastName].filter(Boolean).join(" ");

  if (displayName) {
    sessionStorage.setItem("userDisplayName", displayName);
  } else {
    sessionStorage.removeItem("userDisplayName");
  }
};

const Login = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({});
  const [settings, setSettings] = useState({});
  const [appName, setAppName] = useState(config.name);

  const [state, setState] = useState({
    isForgotPassword: false,
    isTribalMember: false,
    otpRequested: false,
    loginOtpRequested: false,
    userName: "",
    password: "",
    showPassword: false,
    tribalId: "",
    lastName: "",
    otp: "",
    error: "",
    loading: false,
    loadingRequestOTP: false
  });

  // Initialize config and settings
  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);

    const storedSettings = sessionStorage.getItem("portalSettings");
    console.log(storedSettings)
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (e) {
        console.error("Failed to parse portal settings", e);
      }
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("isAuthenticated")) {
      navigate("/home");
      return;
    }

    // Set initial login method based on available options
    const loginOptions = settings.LoginOptions || "member";
    setState(prev => ({
      ...prev,
      isTribalMember: !(loginOptions === "username" || loginOptions === "both")
    }));
  }, [navigate, settings]);

  useEffect(() => {
    if (config.name) {
      setAppName(config.name);
      console.log("appName:", config.name);
    }
  }, [config]);

  const handleAuth = async (params) => {
    if (!navigator.onLine) {
      setState(prev => ({ ...prev, error: "No internet connection" }));
      return { success: false };
    }

    try {
      const response = await fetch(UTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams(params)
      });
      return await response.json();
    } catch (error) {
      console.error("Authentication error:", error);
      setState(prev => ({ ...prev, error: "An error occurred. Please try again." }));
      return { success: false };
    }
  };

  // New API function to get user taxpayers
  const getUserTaxpayers = async () => {
    if (!navigator.onLine) {
      setState(prev => ({ ...prev, error: "No internet connection" }));
      return { success: false, data: [] };
    }

    try {
      const response = await fetch(PBPUTILS + '?action=getusertaxpayers', {
        method: "GET",
        headers: {
          "Accept": "application/json"
        }
      });

      console.log("Get user taxpayers response status:", response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Get user taxpayers data:", data);

      // Handle different response formats
      if (Array.isArray(data)) {
        // Success case - array of taxpayers
        return {
          success: true,
          data: data,
          count: data.length
        };
      } else if (data && data.Status === 'Error') {
        // Error case - no taxpayers found
        console.log("No taxpayers found:", data.Message);
        return {
          success: true, // Still consider this successful for navigation logic
          data: [],
          count: 0,
          message: data.Message
        };
      } else {
        // Unexpected response format
        console.error("Unexpected response format:", typeof data, data);
        return {
          success: false,
          data: [],
          count: 0,
          message: "Invalid data format received"
        };
      }

    } catch (error) {
      console.error("Get user taxpayers error:", error);
      setState(prev => ({ ...prev, error: "Failed to fetch user data" }));
      return {
        success: false,
        data: [],
        count: 0
      };
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    // If username OTP has already been requested, verify the OTP
    if (state.loginOtpRequested) {
      if (!state.otp.trim()) {
        setState(prev => ({ ...prev, error: "One-Time Password is required" }));
        return;
      }

      setState(prev => ({ ...prev, loading: true, error: "" }));

      const data = await handleAuth({
        action: "lgotp",
        otp: state.otp
      });
      console.log("lgotp data", data);

      if (data.status === "success") {
        storeUserDisplayName(data);

        if (config.name === "Prairie Band Potawatomi Nation") {
          // Get user taxpayers data for Prairie Band Potawatomi Nation
          const taxpayersData = await getUserTaxpayers();

          if (taxpayersData.success) {
            console.log("Taxpayers count:", taxpayersData.count);
            console.log("Taxpayers data:", taxpayersData.data);

            // Store taxpayers data in sessionStorage for later use
            sessionStorage.setItem("userTaxpayers", JSON.stringify(taxpayersData.data));
            sessionStorage.setItem("userId", data.userId);

            // Check array count and navigate accordingly
            if (taxpayersData.count === 0) {
              // No taxpayers found - navigate to blapplication
              console.log("No taxpayers found, redirecting to blapplication");
              navigate("/blapplication");
            } else {
              // Taxpayers found - navigate to home
              console.log("Taxpayers found, redirecting to home");
              sessionStorage.setItem("isAuthenticated", "true");

              navigate("/home");
            }
          } else {
            setState(prev => ({
              ...prev,
              error: taxpayersData.message || "Failed to load user data",
              loading: false
            }));
          }
        } else {
          // For other apps, proceed with normal login
          sessionStorage.setItem("isAuthenticated", "true");
          sessionStorage.setItem("userId", data.userId);
          navigate("/home");
        }
      } else {
        setState(prev => ({
          ...prev,
          error: data.message || "Invalid one-time password. Please try again.",
          loading: false
        }));
      }

      return;
    }

    // First step: request One-Time Password for username login
    if (!state.userName.trim() || !state.password.trim()) {
      setState(prev => ({ ...prev, error: "Username and Password are required" }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: "" }));

    const data = await handleAuth({
      action: "login",
      uid: state.userName,
      pwd: state.password
    });
    console.log("data", data);

    if (data.status === "success") {
      setState(prev => ({
        ...prev,
        loginOtpRequested: true,
        loading: false,
        otp: "",
        error: "One-Time Password sent. Please enter the code to continue."
      }));
    } else {
      setState(prev => ({
        ...prev,
        error: data.message || "Invalid username or password",
        loading: false
      }));
    }
  };

  const handleRequestOTP = async (e) => {
    if (e) e.preventDefault();

    if (!state.tribalId.trim() || !state.lastName.trim()) {
      setState(prev => ({ ...prev, error: "Tribal ID and Last Name are required" }));
      return;
    }

    setState(prev => ({ ...prev, loadingRequestOTP: true, error: "" }));

    const data = await handleAuth({
      action: "memauth",
      tid: state.tribalId,
      ln: state.lastName
    });

    if (data.success) {
      setState(prev => ({
        ...prev,
        otpRequested: true,
        error: data.message || "OTP sent successfully",
        loadingRequestOTP: false
      }));
    } else {
      setState(prev => ({
        ...prev,
        error: data.message || "Invalid tribal ID or last name",
        loadingRequestOTP: false
      }));
    }
  };

  const handleVerifyOTP = async (e) => {
    if (e) e.preventDefault();

    if (!state.otp.trim()) {
      setState(prev => ({ ...prev, error: "OTP is required" }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: "" }));

    const data = await handleAuth({ action: "otp", otp: state.otp });

    if (data.success) {
      sessionStorage.setItem("isAuthenticated", "true");
      if (data.userId) {
        sessionStorage.setItem("userId", data.userId);
      }
      navigate("/home");
    } else {
      setState(prev => ({
        ...prev,
        error: data.message || "Invalid OTP. Please try again.",
        loading: false
      }));
    }
  };

  // Handle Enter key press for all forms
  const handleKeyPress = (e, callback) => {
    if (e.key === 'Enter') {
      callback();
    }
  };

  if (state.isForgotPassword) {
    return <ForgotPassword onBack={() => setState(prev => ({ ...prev, isForgotPassword: false }))} />;
  }

  // Don't render until config is loaded
  if (!config.name) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary to-background">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const loginOptions = settings.LoginOptions || "member";
  const homePageLinks = settings.HomePageLinks || [];
  const programTitle = settings.ProgramTitle ? `${settings.ProgramTitle} Portal` : "Portal";

  return (
    <div 
      className={`flex flex-col justify-center items-center min-h-screen px-4 py-8 ${
        appName === "My Tlingit & Haida Portal" ? "bg-gradient-to-br from-[#303030] to-[#000000]" : "bg-gradient-to-br from-primary to-background"
      }`}
    >
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md md:max-w-lg flex flex-col justify-center">
        <div className="flex flex-col items-center mb-8">
          {appName === "My Tlingit & Haida Portal" ? (
            <img src={tlingitLogo} alt="Logo" className="h-16 mb-4" />
          ) : appName === "Klamath Tribes" ? (
            <img src={klLogo} alt="Logo" className="h-32 mb-4" />
          ) : (
            <img src={config.logo} alt="Logo" className="h-16 mb-4" />
          )}
          <h1 className="text-2xl font-bold text-gray-800 mb-1">
            {config.name === "TERO" ? programTitle : config.name}
          </h1>
          {config.name !== "TERO" && (
            <p className="text-gray-600 text-sm">
              {settings.PortalName}
            </p>
          )}
        </div>

        {loginOptions === "both" && (
          <div className="flex justify-center gap-2 mb-6">
            <button
              className={`flex-1 py-2.5 px-4 rounded-lg transition-all duration-300 ${!state.isTribalMember ? "bg-primary text-white shadow-md" : "border border-primary text-primary hover:bg-primary/10"
                }`}
              onClick={() => setState(prev => ({ ...prev, isTribalMember: false, otpRequested: false, error: "" }))}
            >
              Sign In
            </button>
            <button
              className={`flex-1 py-2.5 px-4 rounded-lg transition-all duration-300 ${state.isTribalMember ? "bg-primary text-white shadow-md" : "border border-primary text-primary hover:bg-primary/10"
                }`}
              onClick={() => setState(prev => ({ ...prev, isTribalMember: true, otpRequested: false, error: "" }))}
            >
              Tribal Member
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Username/Password Login Form */}
          {(loginOptions === "username" || (loginOptions === "both" && !state.isTribalMember)) && (
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                {!state.loginOtpRequested ? (
                  <>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Username<span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Username"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                        value={state.userName}
                        onChange={(e) => setState(prev => ({ ...prev, userName: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                      />
                    </div>

                    <div className="space-y-2 relative">
                      <label className="block text-sm font-medium text-gray-700">Password<span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type={state.showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all pr-12"
                          value={state.password}
                          onChange={(e) => setState(prev => ({ ...prev, password: e.target.value }))}
                          onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                          onClick={() => setState(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                        >
                          {state.showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">OTP Verification</label>
                    <input
                      type="text"
                      placeholder="Enter 8-digit code"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                      value={state.otp}
                      onChange={(e) => setState(prev => ({ ...prev, otp: e.target.value }))}
                      onKeyPress={(e) => handleKeyPress(e, handleLogin)}
                    />
                  </div>
                )}

                {state.error && <p className="text-red-500 text-sm">{state.error}</p>}

                <button
                  type="submit"
                  className="w-full bg-primary text-white py-3.5 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%] flex items-center justify-center"
                  disabled={state.loading}
                >
                  {state.loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {state.loginOtpRequested ? "Verifying..." : "Requesting One-Time Password..."}
                    </div>
                  ) : (
                    state.loginOtpRequested ? "Verify & Login" : "Request One-Time Password"
                  )}
                </button>

                <div className="flex flex-col sm:flex-row justify-between items-start gap-2 pt-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setState(prev => ({ ...prev, isForgotPassword: true }))}
                        className="text-sm text-primary hover:text-secondary font-medium underline"
                      >
                        Forgot Password?
                      </button>
                      {/* {homePageLinks.length > 0 && (
                        <button
                          type="button"
                          onClick={() => navigate("/accountRequest")}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium underline"
                        >
                          Request Account
                        </button>
                      )} */}
                    </div>
                  </div>

                  {homePageLinks.length > 0 ? (
                    <div className="flex-1 min-w-0 flex justify-end">
                      <div className="flex flex-wrap justify-end gap-2">
                        {homePageLinks.map((link, index) => (
                          link.URL && (
                            <button
                              key={index}
                              type="button"
                              onClick={() => window.open(link.URL, '_blank')}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              {link.Name}
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  ) : config.name !== "TribeVue Support Portal" ? (
                    <div className="order-1 sm:order-2">
                      <button
                        type="button"
                        onClick={() => navigate(config.name === "Pokagon Band of Potawatomi" ? "/registration" : "/accountRequest")}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium underline"
                      >
                        Request Account
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </form>
          )}

          {/* Tribal Member Login Form */}
          {(loginOptions === "member" || (loginOptions === "both" && state.isTribalMember)) && (
            <>
              {!state.otpRequested ? (
                <form onSubmit={handleRequestOTP}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Tribal ID – Enrollment Number<span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Enter your Tribal ID"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                        value={state.tribalId}
                        onChange={(e) => setState(prev => ({ ...prev, tribalId: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, handleRequestOTP)}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Last Name<span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        placeholder="Enter your Last Name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                        value={state.lastName}
                        onChange={(e) => setState(prev => ({ ...prev, lastName: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, handleRequestOTP)}
                      />
                    </div>

                    {state.error && <p className="text-red-500 text-sm">{state.error}</p>}

                    <button
                      type="submit"
                      className="w-full bg-primary text-white py-3.5 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%]"
                      disabled={state.loadingRequestOTP}
                    >
                      {state.loadingRequestOTP ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Requesting One-Time Password...
                        </div>
                      ) : (
                        "Request One-Time Password"
                      )}
                    </button>

                    {homePageLinks.length > 0 && (
                      <div className="flex gap-4 w-full">
                        <div className="flex-1 flex flex-col gap-2">
                          {homePageLinks.filter((_, index) => index % 2 === 0).map((link, index) => (
                            link.URL && (
                              <button
                                key={index}
                                type="button"
                                onClick={() => window.open(link.URL, '_blank')}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium underline w-full text-left"
                              >
                                {link.Name}
                              </button>
                            )
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                          {homePageLinks.filter((_, index) => index % 2 === 1).map((link, index) => (
                            link.URL && (
                              <button
                                key={index}
                                type="button"
                                onClick={() => window.open(link.URL, '_blank')}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium underline w-full text-left"
                              >
                                {link.Name}
                              </button>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">OTP Verification</label>
                      <input
                        type="text"
                        placeholder="Enter 8-digit code"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
                        value={state.otp}
                        onChange={(e) => setState(prev => ({ ...prev, otp: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, handleVerifyOTP)}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-primary text-white py-3.5 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%]"
                      disabled={state.loading}
                    >
                      {state.loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </div>
                      ) : (
                        "Verify & Login"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {/* Request Account and Contact Us buttons - Always visible for My Tlingit & Haida Portal */}
          {appName === "My Tlingit & Haida Portal" && (
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <button
                type="button"
                onClick={() => navigate("/registration")}
                className="w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%]"
              >
                Request Account
              </button>
              <button
                type="button"
                onClick={() => navigate("/contactus")}
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-secondary transition-all duration-300 shadow-md hover:shadow-lg active:scale-[98%]"
              >
                Contact Us
              </button>
            </div>
          )}
        </div>
      </div>
      <footer className="text-center text-sm text-white mt-8">
        <p>TribeVue® Portal v{BUILD_DATE}</p>
        <p>© 2026 Tribal D, Inc.</p>
      </footer>
    </div>
  );
};

export default Login;